// Executive BI — summary + cashflow + headcount. Uses ECharts for drill-down.
import { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Loading } from "../../shared/components/Empty";

type Summary = { month: string; sales: number; purchase: number; operating_profit: number };
type Cashflow = { cash_balance: number; receivables: number; payables: number; projected_30d: number };
type Headcount = { department_id: number; department: string; headcount: number };

export default function BiPage() {
  const [sum, setSum] = useState<Summary | null>(null);
  const [cash, setCash] = useState<Cashflow | null>(null);
  const [hc, setHc] = useState<Headcount[]>([]);
  const [drill, setDrill] = useState<{ label: string; value: string } | null>(null);

  useEffect(() => {
    api.get<Summary>("/executive/bi/summary").then((r) => setSum(r.data));
    api.get<Cashflow>("/executive/cashflow").then((r) => setCash(r.data));
    api.get<Headcount[]>("/executive/hr-analytics/headcount").then((r) => setHc(r.data));
  }, []);

  if (!sum || !cash) return <><PageHeader title="경영 BI" subtitle="임원 대시보드"/><Loading/></>;

  const cashflowOption = {
    tooltip: { trigger: "item", formatter: "{b}: {c}원" },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: "pie",
      radius: ["45%", "70%"],
      data: [
        { value: Math.round(cash.cash_balance), name: "현금 잔액" },
        { value: Math.round(cash.receivables), name: "미수금" },
        { value: Math.round(cash.payables), name: "미지급금" },
      ],
      emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0,0,0,0.5)" } },
    }],
  };

  const hcOption = {
    tooltip: { trigger: "axis" },
    xAxis: { type: "value" },
    yAxis: {
      type: "category",
      data: hc.map((h) => h.department),
      axisLabel: { fontSize: 11 },
    },
    series: [{
      type: "bar",
      data: hc.map((h) => h.headcount),
      itemStyle: { color: "#3b82f6" },
      label: { show: true, position: "right", fontSize: 11 },
    }],
    grid: { left: 80, right: 40, top: 10, bottom: 10, containLabel: true },
  };

  const salesOption = {
    tooltip: { trigger: "axis" },
    xAxis: { type: "category", data: ["매출", "매입", "영업이익"] },
    yAxis: { type: "value", axisLabel: { formatter: (v: number) => (v / 1e6).toFixed(0) + "M" } },
    series: [{
      type: "bar",
      data: [
        { value: Math.round(sum.sales), itemStyle: { color: "#22c55e" } },
        { value: Math.round(sum.purchase), itemStyle: { color: "#f59e0b" } },
        { value: Math.round(sum.operating_profit), itemStyle: { color: sum.operating_profit >= 0 ? "#3b82f6" : "#ef4444" } },
      ],
    }],
    grid: { left: 60, right: 20, top: 10, bottom: 30 },
  };

  return (
    <>
      <PageHeader title="경영 BI" subtitle={`${sum.month} · 월간 요약`} />

      {/* KPI cards — clickable for drill-down */}
      <div className="t-grid cols-4">
        {[
          { label: `매출 (${sum.month})`, value: Math.round(sum.sales).toLocaleString() + "원" },
          { label: "매입", value: Math.round(sum.purchase).toLocaleString() + "원" },
          { label: "영업이익", value: Math.round(sum.operating_profit).toLocaleString() + "원" },
          { label: "현금 잔액", value: Math.round(cash.cash_balance).toLocaleString() + "원" },
        ].map((k) => (
          <Kpi key={k.label} label={k.label} value={k.value} onClick={() => setDrill(k)} />
        ))}
      </div>

      {/* Drill-down modal */}
      {drill && (
        <div className="t-overlay" onClick={() => setDrill(null)}>
          <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, flex: 1 }}>{drill.label} 상세</h3>
              <button className="t-btn" onClick={() => setDrill(null)}>✕</button>
            </div>
            <div style={{ fontWeight: 700, fontSize: 28, color: "var(--p-600)", marginBottom: 16 }}>{drill.value}</div>
            <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.8 }}>
              <div>· 전월 대비: 데이터 없음 (충분한 이력 필요)</div>
              <div>· 전년 동기: 데이터 없음</div>
              <div>· 목표 달성률: 데이터 없음</div>
            </div>
          </div>
        </div>
      )}

      <div className="t-grid cols-2">
        <div className="t-card">
          <div className="t-card-h"><h3>이번 달 실적</h3></div>
          <ReactECharts option={salesOption} style={{ height: 220 }} />
        </div>
        <div className="t-card">
          <div className="t-card-h"><h3>자금 현황</h3></div>
          <ReactECharts option={cashflowOption} style={{ height: 220 }} />
        </div>
        <div className="t-card" style={{ gridColumn: "span 2" }}>
          <div className="t-card-h"><h3>부서별 인원</h3></div>
          <ReactECharts option={hcOption} style={{ height: Math.max(120, hc.length * 36) }} />
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  return (
    <div className="t-card" style={{ cursor: onClick ? "pointer" : undefined }} onClick={onClick}>
      <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{label}</div>
      <div className="num" style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>{value}</div>
      {onClick && <div style={{ fontSize: 10, color: "var(--p-400)", marginTop: 4 }}>클릭하여 상세보기</div>}
    </div>
  );
}
